import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Package, ShoppingCart, Users, TrendingUp,
  ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';
import { Badge } from '../../components/ui/badge';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) };

        const response = await fetch(`${API}/admin/dashboard`, {
          headers,
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();

          // Fallback for missing monthly_revenue
          if (!data.monthly_revenue || data.monthly_revenue.length === 0) {
            try {
              const reportsRes = await fetch(`${API}/admin/reports/sales?limit=50`, { headers, credentials: 'include' });
              if (reportsRes.ok) {
                const reportsData = await reportsRes.json();
                const sales = reportsData.report || [];

                // Aggregate by month
                const monthlyData = {};
                sales.forEach(order => {
                  const date = new Date(order.created_at);
                  const monthYear = date.toLocaleString('default', { month: 'short', year: '2-digit' });
                  monthlyData[monthYear] = (monthlyData[monthYear] || 0) + (order.total_amount || order.total || 0);
                });

                data.monthly_revenue = Object.entries(monthlyData).map(([month, revenue]) => ({
                  month,
                  revenue
                })).reverse(); // Reverse to show chronological order if sorted by newest
              }
            } catch (err) {
              console.error('Failed to fetch fallback report data:', err);
            }
          }

          setStats(data);
        }
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return 'bg-success/10 text-success';
      case 'shipped': return 'bg-primary/10 text-primary';
      case 'confirmed': return 'bg-warning/10 text-warning';
      case 'cancelled': return 'bg-destructive/10 text-destructive';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 animate-pulse">
              <div className="h-4 bg-surface rounded w-1/2 mb-2" />
              <div className="h-8 bg-surface rounded w-3/4" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Revenue',
      value: formatPrice(stats?.total_revenue || 0),
      icon: TrendingUp,
      color: 'bg-success/10 text-success',
      change: '+12.5%'
    },
    {
      title: 'Total Orders',
      value: stats?.total_orders || 0,
      icon: ShoppingCart,
      color: 'bg-primary/10 text-primary',
      change: '+8.3%'
    },
    {
      title: 'Total Users',
      value: stats?.total_users || 0,
      icon: Users,
      color: 'bg-warning/10 text-warning',
      change: '+15.2%'
    },
    {
      title: 'Total Products',
      value: stats?.total_products || 0,
      icon: Package,
      color: 'bg-secondary/10 text-secondary',
      change: '+3.1%'
    }
  ];

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl md:text-3xl text-secondary">Dashboard</h1>
        <p className="text-sm text-muted-foreground font-body">
          Last updated: {new Date().toLocaleTimeString()}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, idx) => (
          <div key={idx} className="bg-white rounded-2xl p-6 shadow-soft" data-testid={`stat-${stat.title.toLowerCase().replace(' ', '-')}`}>
            <div className="flex items-center justify-between mb-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${stat.color}`}>
                <stat.icon className="w-5 h-5" />
              </div>
              <span className="flex items-center gap-1 text-xs text-success font-body">
                <ArrowUpRight className="w-3 h-3" />
                {stat.change}
              </span>
            </div>
            <p className="text-sm text-muted-foreground font-body mb-1">{stat.title}</p>
            <p className="font-heading text-2xl text-secondary">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Charts & Tables */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Revenue Chart */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-soft">
          <h2 className="font-heading text-lg text-secondary mb-6">Revenue Overview</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats?.monthly_revenue || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" stroke="#86868B" fontSize={12} tickLine={false} />
                <YAxis stroke="#86868B" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#0071E3"
                  strokeWidth={3}
                  dot={{ fill: '#0071E3', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Order Stats */}
        <div className="bg-white rounded-2xl p-6 shadow-soft">
          <h2 className="font-heading text-lg text-secondary mb-6">Order Status</h2>
          <div className="space-y-4">
            {[
              { label: 'Pending', count: stats?.pending_orders || 0, color: 'bg-muted-foreground' },
              { label: 'Shipped', count: stats?.shipped_orders || 0, color: 'bg-primary' },
              { label: 'Delivered', count: stats?.delivered_orders || 0, color: 'bg-success' }
            ].map((item, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${item.color}`} />
                  <span className="font-body text-secondary">{item.label}</span>
                </div>
                <span className="font-body font-semibold text-secondary">{item.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-2xl p-6 shadow-soft">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-lg text-secondary">Recent Orders</h2>
          <Link to="/admin/orders" className="text-sm text-primary hover:underline font-body">
            View All
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-3 px-4 font-body font-medium text-muted-foreground text-sm">Order ID</th>
                <th className="text-left py-3 px-4 font-body font-medium text-muted-foreground text-sm">Customer</th>
                <th className="text-left py-3 px-4 font-body font-medium text-muted-foreground text-sm">Date</th>
                <th className="text-left py-3 px-4 font-body font-medium text-muted-foreground text-sm">Amount</th>
                <th className="text-left py-3 px-4 font-body font-medium text-muted-foreground text-sm">Status</th>
              </tr>
            </thead>
            <tbody>
              {stats?.recent_orders && stats.recent_orders.length > 0 ? (
                stats.recent_orders.slice(0, 5).map((order) => (
                  <tr key={order.order_id} className="border-b border-gray-50 hover:bg-surface/50">
                    <td className="py-4 px-4">
                      <Link to={`/admin/orders/${order.order_id}`} className="font-body text-primary hover:underline">
                        #{order.order_id}
                      </Link>
                    </td>
                    <td className="py-4 px-4">
                      <p className="font-body text-secondary">{order.user_name}</p>
                      <p className="font-body text-xs text-muted-foreground">{order.user_email}</p>
                    </td>
                    <td className="py-4 px-4 font-body text-muted-foreground">
                      {formatDate(order.created_at)}
                    </td>
                    <td className="py-4 px-4 font-body font-medium text-secondary">
                      {formatPrice(order.total)}
                    </td>
                    <td className="py-4 px-4">
                      <Badge className={`${getStatusColor(order.status)} capitalize`}>
                        {order.status}
                      </Badge>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-muted-foreground font-body">
                    No orders yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
